import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios from "axios"
import { deletedArticlesSlice } from "./deletedArticlesSlice"
import { unassignedArticlesEventsSlice } from "./unassignedArticlesEventsSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

// Define the initial state of the search UI
export const CITATION_IMPORT_VIEW_MAP = {
	SEARCH: 1,
	RESULTS: 2
}

// Helper function to check if two articles match based on their IDs
function isMatchingArticle(articleA, articleB) {
	const idsToCheck = ["DOI", "PMID", "ISBN", "PMCID", "ARXIVID"]
	return idsToCheck.some(id => {
		if (!articleA[id]) {
			return false
		}
		return articleA[id]?.toUpperCase() === articleB[id]?.toUpperCase()
	}	
	)
}

// Define the thunk action for fetching data
export const fetchData = createAsyncThunk(
	"citationImport/fetchData",
	async ({ workspaceId, query }, thunkAPI) => {
		thunkAPI.dispatch(startSearch(workspaceId))
		try {
			const response = await axios.get(`/api/workspace/${workspaceId}/zotero/${encodeURIComponent(query.toUpperCase())}`)
			return {data: response.data, workspaceId}
		} catch (error) {
			return thunkAPI.rejectWithValue(error.response.data)
		}
	}
)

const initialState = {
	query: "",          // The search query entered by the user
	results: [],        // Search results
	isLoading: false,   // Flag to indicate if the search is in progress
	error: null,         // Error information, if any
	view: CITATION_IMPORT_VIEW_MAP.SEARCH
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)
export const citationImportSlice = createSlice({
	name: "citationImport",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		// Action to set view and reset state
		setView(state, action) {
			const {workspaceId, view} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.view = view
			workspace.isLoading = initialState.isLoading
			workspace.error = initialState.error
			workspace.results = initialState.results
			workspace.query = initialState.query
		},

		// Action to update the search query
		setQuery(state, action) {
			const { query, workspaceId } = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.query = query
		},

		setCitationAsImported(state, action) {
			const {article, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const index = workspace.results.findIndex((a) => {
				const articleIsDeleted = a.articleId && a.deletedAt !== null
				if (articleIsDeleted) {
					//TODO: keep an eye on this one... do we still need to reference a.article with the changes to deleted slices??
					return isMatchingArticle(a.article, article.article)
				}
				return isMatchingArticle(a, article.article)
			})

			if (index > -1) {
				workspace.results[index] = article
			}
		},
		// Action to start the search
		startSearch(state, action) {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.view = CITATION_IMPORT_VIEW_MAP.RESULTS
		},
	
		// Action to set the search results TODO: currently unused
		setSearchResults(state, action) {
			const {results, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.results = results
			workspace.isLoading = false
		},
	
		// Action to handle search failure
		searchFailed(state, action) {
			const {error, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.error = error
			workspace.isLoading = false
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchData.pending, (state, action) => {
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = true
				workspace.error = null
			})
			.addCase(fetchData.fulfilled, (state, action) => {
				const {data: results, workspaceId} = action.payload
				citationImportSlice.caseReducers.setSearchResults(state, {payload: {results, workspaceId}})
			})
			.addCase(fetchData.rejected, (state, action) => {
				const {workspaceId} = action.meta.arg
				citationImportSlice.caseReducers.searchFailed(state, {payload: {error: action.payload, workspaceId}})
			})
			.addCase(deletedArticlesSlice.actions.addDeletedArticle, (state, action) => {
				const {article: newlyDeletedArticle, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)

				const article = workspace.results.find(a => a.articleId === newlyDeletedArticle.articleId)
				if (article) {
					article.deletedAt = newlyDeletedArticle.deletedAt
				}
			})
			.addCase(deletedArticlesSlice.actions.removeDeletedArticle, (state, action) => {
				const {articleId, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				const article = workspace.results.find(a => a.articleId === articleId)
				if (article) {
					article.deletedAt = null
				}
			})
			.addCase(unassignedArticlesEventsSlice.actions.concatUnassignedArticles, (state, action) => {
				const {articles: incomingUnassignedArticles, workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)

				workspace.results.forEach((resultantArticle, index) => {
					const isImportedResult = resultantArticle.article
					if (!isImportedResult) {
						const matchingIndex = incomingUnassignedArticles.findIndex((a) => {
							return isMatchingArticle(resultantArticle, a)
						})
						if (matchingIndex > -1) {
							workspace.results[index] = incomingUnassignedArticles[matchingIndex]
						}
					}
				})
			})
	},
})

export const { setView, setQuery, startSearch, setSearchResults, searchFailed, setCitationAsImported } = citationImportSlice.actions


export const selectQuery = (state, workspaceId) => state.citationImport.byWorkspace[workspaceId]?.query ?? initialState.query

export const selectResults = (state, workspaceId) =>
	state.citationImport.byWorkspace?.[workspaceId]?.results ?? initialState.results

export const selectIsLoading = (state, workspaceId) =>
	state.citationImport.byWorkspace?.[workspaceId]?.isLoading ?? initialState.isLoading

export const selectError = (state, workspaceId) =>
	state.citationImport.byWorkspace?.[workspaceId]?.error ?? initialState.error

export const selectView = (state, workspaceId) =>
	state.citationImport.byWorkspace?.[workspaceId]?.view ?? initialState.view

export default citationImportSlice.reducer
